import { captureException } from '@sentry/nextjs';
import { useSnackbar } from 'notistack';
import { useCallback, useState } from 'react';

import Backdrop from '@/components/Module/Backdrop';
import Checkbox from '@/components/Module/Checkbox';
import PhonenumberInput from '@/components/Module/PhonenumberInput';
import PrimaryButton from '@/components/Module/PrimaryButton';
import SecondaryButton from '@/components/Module/SecondaryButton';
import Spinner from '@/components/Module/Spinner';
import TextInput from '@/components/Module/TextInput';
import Title from '@/components/Module/Title';
import useViewport from '@/hooks/useViewport';
import { AuthType } from '@/types/auth';

export type MyProfileProps = {
  email: string;
  firstName: string;
  lastName: string;
  mm: string;
  dd: string;
  yyyy: string;
  phonenumber: string;
  consentedToMarketing: boolean;
  onSubmit: (params: {
    consentedToMarketing: boolean;
    firstName: string;
    lastName: string;
    phonenumber: string;
  }) => unknown;
  authType: AuthType;
  resetPassword: () => Promise<string>;
  logout: () => unknown;
};

const MyProfileContents = (props: MyProfileProps) => {
  const { email, authType, logout } = props;
  const [firstName, setFirstName] = useState(props.firstName);
  const [lastName, setLastName] = useState(props.lastName);
  const [phonenumber, setPhonenumber] = useState(props.phonenumber);
  const [consentedToMarketing, setConsentedToMarketing] = useState(
    props.consentedToMarketing,
  );

  const anyChanges =
    firstName !== props.firstName ||
    lastName !== props.lastName ||
    phonenumber !== props.phonenumber ||
    consentedToMarketing !== props.consentedToMarketing;

  const [isLoading, setIsLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const submit = useCallback(async () => {
    try {
      setIsLoading(true);
      await props.onSubmit({
        consentedToMarketing,
        phonenumber,
        firstName,
        lastName,
      });
      enqueueSnackbar(`Profile has been updated.`);
      setIsLoading(false);
    } catch (error) {
      if (error instanceof Error) {
        enqueueSnackbar(error.message, { variant: `error` });
        captureException(error);
      }
    }
    setIsLoading(false);
  }, [
    consentedToMarketing,
    enqueueSnackbar,
    firstName,
    lastName,
    phonenumber,
    props,
  ]);

  const resetPassword = useCallback(async () => {
    try {
      setIsLoading(true);
      const message = await props.resetPassword();

      enqueueSnackbar(message);
      setIsLoading(false);
    } catch (error) {
      if (error instanceof Error) {
        enqueueSnackbar(error.message, { variant: `error` });
        captureException(error);
      }
    }
    setIsLoading(false);
  }, [enqueueSnackbar, props]);

  return (
    <div className={`px-[24px]`}>
      {isLoading && (
        <Backdrop zIndex={10}>
          <div className={`fixed h-full w-full bg-gray-100 opacity-50`} />
          <Spinner />
        </Backdrop>
      )}
      <Title>My Profile</Title>
      <div className={`mb-[80px] flex flex-col items-center`}>
        <TextInput
          label={`Email`}
          value={email}
          placeholder={`Email`}
          setValue={() => null}
          disabled={true}
        />
        {authType === `email` && (
          <TextInput
            label={`Password`}
            value={`*********`}
            setValue={() => null}
            disabled={true}
            button={{
              text: `Reset`,
              onSubmit: resetPassword,
            }}
          />
        )}
        <div className={`flex w-full gap-[9px]`}>
          <TextInput
            label={`First Name`}
            value={firstName}
            placeholder={`ex) John`}
            setValue={setFirstName}
          />
          <TextInput
            label={`Last Name`}
            value={lastName}
            placeholder={`ex) Smith`}
            setValue={setLastName}
          />
        </div>
        <label>
          <div className={`flex w-full`}>
            <span className={`font-roboto text-b2 font-normal text-gray-100`}>
              Date of Birth (mm/dd/yyyy)
            </span>
          </div>
          <div className={`flex w-full gap-[9px]`}>
            <TextInput
              label={``}
              value={props.mm}
              placeholder={`ex) 01`}
              disabled={true}
            />
            <TextInput
              label={``}
              value={props.dd}
              placeholder={`ex) 01`}
              disabled={true}
            />
            <TextInput
              label={``}
              value={props.yyyy}
              placeholder={`ex) 2001`}
              disabled={true}
            />
          </div>
        </label>
        <PhonenumberInput
          placeholder={`+1 (234) 567-8901`}
          label={`Phone number`}
          value={phonenumber}
          setValue={setPhonenumber}
        />
      </div>
      <div className={`mb-[56px]`}>
        <label className={`flex gap-[10px]`}>
          <Checkbox
            checked={consentedToMarketing}
            setChecked={setConsentedToMarketing}
          ></Checkbox>
          <p className={`font-roboto text-b2 font-normal text-gray-600`}>
            Receive emails about updates and personalized event recommendations
          </p>
        </label>
      </div>

      <div className={`flex flex-col gap-[16px]`}>
        {anyChanges && (
          <PrimaryButton onClick={submit}>Save changes</PrimaryButton>
        )}
        <SecondaryButton destructive={true} onClick={logout}>
          Log out
        </SecondaryButton>
      </div>
    </div>
  );
};
const MobileMyProfile = (props: MyProfileProps) => {
  const {} = props;

  return <MyProfileContents {...props}></MyProfileContents>;
};
const DesktopMyProfile = (props: MyProfileProps) => {
  const {} = props;

  return (
    <div className={`mx-auto flex w-[458px]`}>
      <MyProfileContents {...props}></MyProfileContents>
    </div>
  );
};
const MyProfile = (props: MyProfileProps) => {
  const {} = props;
  const viewport = useViewport();

  if (viewport == `mobile`) {
    return <MobileMyProfile {...props} />;
  }
  if (viewport == `tablet` || viewport == `desktop`) {
    return <DesktopMyProfile {...props} />;
  }

  return null;
};

export default MyProfile;
